(() => {
  const selector = '[data-product-shipping-countdown]';

  const pad = (value) => String(value).padStart(2, '0');

  // "02:13:05" — hours, minutes, seconds left before today's cutoff.
  const formatClock = (totalSeconds) =>
    `${pad(Math.floor(totalSeconds / 3600))}:${pad(Math.floor((totalSeconds % 3600) / 60))}:${pad(totalSeconds % 60)}`;

  // 15 -> "15:00", a half day's 12.5 -> "12:30".
  const formatHour = (hour) => `${pad(Math.floor(hour))}:${pad(Math.round((hour % 1) * 60))}`;

  const dispatchDateFormatter = new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric', month: 'long', weekday: 'long', timeZone: 'UTC',
  });
  const lower = (text) => text.toLocaleLowerCase('tr-TR');

  // Second line of a not-today promise: the dispatch date, minus whatever the
  // title already says. "En geç yarın kargoda" -> "15 Ekim Perşembe",
  // "En geç pazartesi kargoda" -> "19 Ekim", "21 Ekim tarihinde kargoda" -> "".
  const dispatchDetail = (isoDate, title) => {
    const parts = dispatchDateFormatter.formatToParts(Date.parse(isoDate));
    const part = (type) => parts.find((p) => p.type === type)?.value || '';
    const date = `${part('day')} ${part('month')}`;
    const weekday = part('weekday');
    const said = lower(title);

    if (said.includes(lower(date))) return '';
    if (said.includes(lower(weekday))) return date;
    return `${date} ${weekday}`;
  };

  // Today's second line. The ticking clock is aria-hidden: a screen reader
  // gets the cutoff once, as a time of day, instead of a number that changes
  // every second.
  const buildTodayDetail = (detail, cutoffHour) => {
    const visible = document.createElement('span');
    visible.setAttribute('aria-hidden', 'true');
    const time = document.createElement('span');
    time.className = 'product-shipping-countdown__time';
    time.dataset.productShippingCountdownTime = '';
    visible.append(time, ' içinde sipariş verin');

    const spoken = document.createElement('span');
    spoken.className = 'visually-hidden';
    spoken.textContent = `Son sipariş saati ${formatHour(cutoffHour)}`;

    detail.replaceChildren(visible, spoken);
  };

  const render = (elements) => {
    elements.forEach((element) => {
      const title = element.querySelector('[data-product-shipping-countdown-title]');
      const detail = element.querySelector('[data-product-shipping-countdown-detail]');
      if (!title || !detail) return;

      const promise = window.ShippingPromise.resolve({ handle: element.dataset.productHandle || '' });
      // Same words as the card badge (deadlineText is shared copy); only the
      // exclamation mark goes — this is a label, not a shout.
      const titleText = promise.shipsToday ? 'Bugün kargoda' : String(promise.deadlineText || '').replace(/!$/, '');
      const key = `${titleText}|${promise.dispatchDate}|${promise.cutoffHour ?? ''}`;

      // DOM is rebuilt only when the promise itself changes: first fill, the
      // cutoff, midnight.
      if (element.dataset.promiseKey !== key) {
        element.dataset.promiseKey = key;
        element.dataset.state = promise.shipsToday ? 'today' : 'later';
        title.textContent = titleText;
        if (promise.shipsToday) buildTodayDetail(detail, promise.cutoffHour);
        else detail.textContent = dispatchDetail(promise.dispatchDate, titleText);
      }

      // The per-second tick writes one text node, outside the live region.
      if (promise.shipsToday) {
        const time = detail.querySelector('[data-product-shipping-countdown-time]');
        const clock = formatClock(promise.secondsUntilCutoff);
        if (time && time.textContent !== clock) time.textContent = clock;
      }

      // Live region switched on only AFTER the first fill: page load stays
      // silent, and the one thing ever announced is a real change of promise
      // (15:00 -> "En geç yarın kargoda").
      if (!title.hasAttribute('aria-live')) title.setAttribute('aria-live', 'polite');
    });
  };

  let timer = null;

  // The countdown only exists on product pages and inside quick-view, but this
  // script is loaded site-wide. Check for a target BEFORE doing any clock work,
  // and keep the ticker off while there is nothing to tick — on a page with no
  // countdown this costs one querySelectorAll instead of a resolve() plus a
  // full-document scan every second.
  const sync = () => {
    // Guarded here and not only in init(), because the product page calls
    // sync() itself from an inline script (see product-shipping-countdown.liquid)
    // and must not throw if shipping-promise.js failed to load.
    if (!window.ShippingPromise) return;

    const elements = document.querySelectorAll(selector);

    if (!elements.length) {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
      return;
    }

    if (timer === null) timer = window.setInterval(sync, 1000);
    render(elements);
  };

  let queued = false;
  const queueSync = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  };

  const init = () => {
    if (!window.ShippingPromise) {
      // Bail rather than reimplement the rules locally — a second copy of them
      // is exactly how the product page and the cards drifted apart before.
      console.warn('product-shipping-countdown: shipping-promise.js did not load.');
      return;
    }

    sync();

    // Coalesced through rAF. The unthrottled version ran a full-document scan
    // synchronously inside every mutation batch on the page — carousel slide
    // changes, app embeds and the cart drawer all paid for it.
    new MutationObserver(queueSync).observe(document.body, { childList: true, subtree: true });

    // Belt and braces: quick-view injects its markup asynchronously and is the
    // one place outside a product page that can introduce a countdown.
    document.addEventListener('quick-view:loaded', queueSync);
  };

  // The product page fills its row before this script's DOMContentLoaded turn
  // comes round: product-shipping-countdown.liquid calls sync() the moment its
  // markup is parsed, which is why theme.liquid loads this file WITHOUT defer on
  // product pages. The row's height is reserved either way (pending is the full
  // two lines under visibility:hidden), so this is about never painting a blank
  // row, not about layout shift. Everywhere else the DOMContentLoaded path below
  // still owns it.
  window.ProductShippingCountdown = { sync };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
